import { type CommunityTestimonial, getInitials } from "@upsquad/shared";
import { Quote } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "@/src/common/components/ui";
import { EyebrowBadge } from "../atoms/eyebrow-badge";
import { SectionHeading } from "../atoms/section-heading";

interface LandingTestimonialsSectionProps {
	testimonials: CommunityTestimonial[];
}

export function LandingTestimonialsSection({ testimonials }: LandingTestimonialsSectionProps) {
	const { t } = useTranslation("community");
	const sorted = useMemo(() => [...testimonials].sort((a, b) => a.order - b.order), [testimonials]);

	if (sorted.length === 0) return null;

	return (
		<section className="landing-bg-mesh py-24 md:py-32">
			<div className="mx-auto flex w-full max-w-7xl flex-col items-center gap-16 px-6">
				<SectionHeading
					eyebrow={<EyebrowBadge tone="ink">{t("landing.testimonials.eyebrow")}</EyebrowBadge>}
					title={t("landing.testimonials.title")}
				/>
				<div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
					{sorted.map((item, idx) => (
						<figure
							key={`${item.author_name}-${idx}`}
							className="landing-shadow-card flex flex-col gap-5 rounded-2xl border border-divider bg-content1 p-6"
						>
							<Quote className="h-6 w-6 text-accent/40" aria-hidden />
							<blockquote className="text-base leading-relaxed text-foreground/80">{item.quote}</blockquote>
							<figcaption className="mt-auto flex items-center gap-3">
								<Avatar size="sm">
									{item.author_avatar_url && <Avatar.Image src={item.author_avatar_url} alt={item.author_name} loading="lazy" />}
									<Avatar.Fallback>{getInitials(item.author_name)}</Avatar.Fallback>
								</Avatar>
								<div>
									<p className="text-sm font-medium text-foreground">{item.author_name}</p>
									{item.author_role && <p className="text-xs text-foreground/50">{item.author_role}</p>}
								</div>
							</figcaption>
						</figure>
					))}
				</div>
			</div>
		</section>
	);
}
